import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  HELP_IMAGE_BYTES_MAX,
  HELP_IMAGES_MAX,
  HELP_MESSAGE_MAX,
  compareDottedVersions,
  filterHelpActions,
  filterHelpLinks,
  filterHelpSources,
  helpAskInputSchema,
  helpEventSchema,
  helpGuideSchema,
  helpPackInfoSchema,
  helpScreenKeyForPath,
  helpStatusSchema,
  type HelpAction,
  type HelpContext,
  type HelpEngineErrorCode,
  type HelpEvent,
  type HelpGuideResult,
  type HelpImageInput,
  type HelpLink,
  type HelpMessage,
  type HelpPackInfo,
  type HelpRunPhase,
  type HelpScreenKey,
  type HelpSource,
  type HelpStatus,
  type HelpTurnOutcome,
  type HelpVerdict,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { useToolboxAccess } from "@/hooks/queries/use-toolbox-access";
import { useHelpPanelStore } from "@/stores/help-panel-store";
import { projectFromPath } from "@/lib/grooming/paths";
import { toolsetSupportOf, useSpecTreeVersion, useToolboxBridge } from "@/lib/toolbox/bridge";
import {
  HELP_IMAGE_LIMIT_MESSAGE,
  HELP_IMAGE_SIZE_MESSAGE,
  HELP_IMAGE_TYPE_MESSAGE,
  HELP_SCREEN_LABELS,
  helpErrorMessage,
  helpItemIdFromLocation,
  helpRolesFor,
  isHelpImageMimeType,
  newHelpConversationId,
  readFileAsBase64,
} from "@/lib/toolbox/help";

/**
 * The SpecTree Toolbox Help panel's link to `window.spectreeToolbox.help`
 * (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1557, ER-1655 Resolved
 * Decisions 1, 7 and 8).
 *
 * - Whether Help can be used: the app ships the help toolset and its contract
 *   is inside SpecTree's range (`compareContract`), and the engine reports
 *   `ready` (`getStatus()` and `status-changed`).
 * - The bundled help pack (`getPackInfo()`): suggested questions per screen,
 *   guides, and the SpecTree version it describes (out-of-date notice).
 * - One conversation in memory only: never stored, no history across days.
 *   `newConversation` ends the old one (the app deletes its folder) and
 *   starts a fresh id. Answer text streams from `answer-delta` events; every
 *   event is parsed with `helpEventSchema.safeParse` and ignored otherwise.
 * - The context sent with every question: route, page title, roles, project,
 *   item on screen and the app, SpecTree and pack versions.
 * - Readiness is checked again while the open panel shows Claude signed out
 *   or missing (SpecTree Windows walk 1, gap G-03): every
 *   {@link HELP_NOT_READY_RECHECK_MS}, when the window gets the focus back and
 *   when Sign in to Claude returns. Once Help is ready again after a sign-out
 *   the panel says {@link HELP_SIGNED_IN_NOTICE}.
 */

/** How often the open panel asks again while Claude is signed out or missing (gap G-03). */
export const HELP_NOT_READY_RECHECK_MS = 5_000;

/** Shown once Help is ready again after Claude was signed out (gap G-03). */
export const HELP_SIGNED_IN_NOTICE = "Signed in — Help is ready";

/**
 * True for the not-ready states the person fixes outside the panel (sign in,
 * install Claude Code), which the panel must notice by asking again. A usage
 * limit is re-checked by the app at the reset; missing files need an update.
 * Pure; exported for tests.
 */
export function helpStatusNeedsRecheck(status: HelpStatus | null): boolean {
  return status?.state === "signed-out" || status?.state === "claude-missing";
}

/** A screenshot waiting to be sent, or sent with a question. */
export interface HelpAttachment {
  id: string;
  image: HelpImageInput;
  /** A `data:` URL for the thumbnail. */
  previewUrl: string;
}

export interface HelpPersonMessage {
  id: string;
  role: "person";
  text: string;
  images: HelpAttachment[];
  /** The page context sent with the question (route, roles, versions); Send to Aaron reuses it. */
  context?: HelpContext;
}

export interface HelpAnswerMessage {
  id: string;
  role: "help";
  /** Null until the engine names the turn. */
  turnId: string | null;
  /** Raw streamed text; show it through `helpDisplayText`. */
  text: string;
  streaming: boolean;
  phase?: HelpRunPhase;
  activity?: string;
  outcome?: HelpTurnOutcome;
  verdict?: HelpVerdict;
  sources: HelpSource[];
  links: HelpLink[];
  actions: HelpAction[];
  errorCode?: HelpEngineErrorCode;
  errorMessage?: string;
}

export type HelpChatMessage = HelpPersonMessage | HelpAnswerMessage;

/**
 * What the panel shows:
 * - `browser`: no SpecTree Toolbox (the panel never mounts there);
 * - `app-update-needed`: the app has no help toolset, or an older contract;
 * - `spectree-update-needed`: the app is newer than this SpecTree serves;
 * - `loading`: waiting for the engine's first status;
 * - `not-ready`: the engine cannot answer now (signed out, Claude missing, usage limit, …);
 * - `ready`: questions can be asked.
 */
export type HelpAvailability =
  | "browser"
  | "app-update-needed"
  | "spectree-update-needed"
  | "loading"
  | "not-ready"
  | "ready";

/** Decide availability. Pure; exported for tests. */
export function helpAvailabilityOf(
  bridge: ToolboxBridge | null,
  support: ReturnType<typeof toolsetSupportOf>,
  status: HelpStatus | null,
  statusFailed: boolean
): HelpAvailability {
  if (!bridge) return "browser";
  if (!bridge.help || bridge.app.contracts.help === undefined) return "app-update-needed";
  if (support === "app-update-needed") return "app-update-needed";
  if (support === "spectree-update-needed") return "spectree-update-needed";
  if (status === null) return statusFailed ? "ready" : "loading";
  return status.state === "ready" ? "ready" : "not-ready";
}

// ---------------------------------------------------------------------------
// Conversation reducer
// ---------------------------------------------------------------------------

interface ConversationState {
  conversationId: string;
  messages: HelpChatMessage[];
  /** The answer being written, or null. */
  activeAnswerId: string | null;
}

type ConversationAction =
  | { type: "reset"; conversationId: string }
  | { type: "ask-started"; person: HelpPersonMessage; answerId: string }
  | { type: "ask-resolved"; answerId: string; turnId: string }
  | {
      type: "ask-failed";
      answerId: string;
      errorCode: HelpEngineErrorCode | undefined;
      message: string;
    }
  | { type: "event"; event: Exclude<HelpEvent, { type: "status-changed" }> };

function updateAnswer(
  state: ConversationState,
  answerId: string,
  change: (answer: HelpAnswerMessage) => HelpAnswerMessage
): HelpChatMessage[] {
  return state.messages.map((message) =>
    message.role === "help" && message.id === answerId ? change(message) : message
  );
}

/** The answer an event belongs to: the one with its turn id, or the active one still waiting for an id. */
function targetAnswerId(state: ConversationState, turnId: string): string | null {
  const byTurn = state.messages.find(
    (message) => message.role === "help" && message.turnId === turnId
  );
  if (byTurn) return byTurn.id;
  const active = state.messages.find((message) => message.id === state.activeAnswerId);
  if (active?.role === "help" && active.turnId === null) return active.id;
  return null;
}

/** Apply one conversation change. Pure; exported for tests. */
export function helpConversationReducer(
  state: ConversationState,
  action: ConversationAction
): ConversationState {
  switch (action.type) {
    case "reset":
      return { conversationId: action.conversationId, messages: [], activeAnswerId: null };
    case "ask-started": {
      const answer: HelpAnswerMessage = {
        id: action.answerId,
        role: "help",
        turnId: null,
        text: "",
        streaming: true,
        phase: "starting",
        sources: [],
        links: [],
        actions: [],
      };
      return {
        ...state,
        messages: [...state.messages, action.person, answer],
        activeAnswerId: action.answerId,
      };
    }
    case "ask-resolved":
      return {
        ...state,
        messages: updateAnswer(state, action.answerId, (answer) =>
          answer.turnId === null ? { ...answer, turnId: action.turnId } : answer
        ),
      };
    case "ask-failed":
      return {
        ...state,
        activeAnswerId: state.activeAnswerId === action.answerId ? null : state.activeAnswerId,
        messages: updateAnswer(state, action.answerId, (answer) => ({
          ...answer,
          streaming: false,
          outcome: "failed",
          ...(action.errorCode ? { errorCode: action.errorCode } : {}),
          errorMessage: action.message,
        })),
      };
    case "event": {
      const { event } = action;
      if (event.conversationId !== state.conversationId) return state;
      const answerId = targetAnswerId(state, event.turnId);
      if (answerId === null) return state;
      const withTurn = (answer: HelpAnswerMessage): HelpAnswerMessage =>
        answer.turnId === null ? { ...answer, turnId: event.turnId } : answer;
      switch (event.type) {
        case "turn-started":
          return { ...state, messages: updateAnswer(state, answerId, withTurn) };
        case "answer-delta":
          return {
            ...state,
            messages: updateAnswer(state, answerId, (answer) =>
              answer.streaming
                ? { ...withTurn(answer), text: answer.text + event.text, phase: "writing" }
                : answer
            ),
          };
        case "turn-progress":
          return {
            ...state,
            messages: updateAnswer(state, answerId, (answer) =>
              answer.streaming
                ? {
                    ...withTurn(answer),
                    phase: event.phase,
                    ...(event.activity ? { activity: event.activity } : {}),
                  }
                : answer
            ),
          };
        case "turn-finished":
          return {
            ...state,
            activeAnswerId: state.activeAnswerId === answerId ? null : state.activeAnswerId,
            messages: updateAnswer(state, answerId, (answer) => {
              const done: HelpAnswerMessage = {
                ...withTurn(answer),
                streaming: false,
                outcome: event.outcome,
              };
              if (event.outcome === "answered") {
                return {
                  ...done,
                  text: event.answer ?? answer.text,
                  ...(event.verdict ? { verdict: event.verdict } : {}),
                  sources: filterHelpSources(event.sources),
                  links: filterHelpLinks(event.links),
                  actions: filterHelpActions(event.actions),
                };
              }
              if (event.outcome === "failed") {
                return {
                  ...done,
                  ...(event.errorCode ? { errorCode: event.errorCode } : {}),
                  errorMessage: helpErrorMessage(event.errorCode, event.message),
                };
              }
              return done;
            }),
          };
      }
    }
  }
}

/** The conversation as `help.ask` carries it: answered text only, oldest first. */
function historyOf(messages: readonly HelpChatMessage[]): HelpMessage[] {
  const history: HelpMessage[] = [];
  for (const message of messages) {
    if (message.role === "person")
      history.push({ role: "person", text: message.text.slice(0, HELP_MESSAGE_MAX) });
    else if (message.outcome === "answered" && message.text.trim().length > 0)
      history.push({ role: "help", text: message.text.slice(0, HELP_MESSAGE_MAX) });
  }
  return history.slice(-200);
}

/** "2.12.3" as "2.12": a patch release alone never makes the notes out of date. */
function majorMinorOf(version: string): string {
  return version.split(".").slice(0, 2).join(".");
}

let attachmentSeq = 0;
function nextId(prefix: string): string {
  attachmentSeq += 1;
  return `${prefix}-${String(attachmentSeq)}`;
}

function attachmentOf(image: HelpImageInput): HelpAttachment {
  return {
    id: nextId("help-image"),
    image,
    previewUrl: `data:${image.mimeType};base64,${image.dataBase64}`,
  };
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

export interface HelpBridgeView {
  availability: HelpAvailability;
  status: HelpStatus | null;
  packInfo: HelpPackInfo | null;
  /** The screen on view, for suggestions and the context line. */
  screenKey: HelpScreenKey;
  screenLabel: string;
  project: string | undefined;
  roles: string[];
  messages: HelpChatMessage[];
  asking: boolean;
  draft: string;
  setDraft: (value: string) => void;
  attachments: HelpAttachment[];
  /** The last attachment problem in plain words, or null. */
  attachError: string | null;
  addImageFiles: (files: readonly File[]) => Promise<void>;
  removeAttachment: (id: string) => void;
  captureScreen: () => Promise<void>;
  /** Shown under the header for the whole conversation once the pack is older than the server. */
  outOfDate: { packVersion: string | undefined; serverVersion: string | undefined } | null;
  ask: (question?: string) => Promise<void>;
  stop: () => void;
  newConversation: () => void;
  getGuide: (id: string) => Promise<HelpGuideResult>;
  /** Ask the app again whether Help can answer (after Sign in to Claude returns; gap G-03). */
  recheckStatus: () => void;
  /** {@link HELP_SIGNED_IN_NOTICE} once Help is ready again after a sign-out, until the next question; else null. */
  readyNotice: string | null;
}

export function useHelpBridge(): HelpBridgeView {
  const bridge = useToolboxBridge();
  const help = bridge?.help ?? null;
  const location = useLocation();
  const version = useSpecTreeVersion();
  const { data: access } = useToolboxAccess();

  const [status, setStatus] = useState<HelpStatus | null>(null);
  const [statusFailed, setStatusFailed] = useState(false);
  const [packInfo, setPackInfo] = useState<HelpPackInfo | null>(null);
  const [conversation, dispatch] = useReducer(helpConversationReducer, undefined, () => ({
    conversationId: newHelpConversationId(),
    messages: [],
    activeAnswerId: null,
  }));
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<HelpAttachment[]>([]);
  const attachmentsRef = useRef<HelpAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [outOfDateLatched, setOutOfDateLatched] = useState(false);
  const [readyNotice, setReadyNotice] = useState<string | null>(null);
  const panelOpen = useHelpPanelStore((state) => state.open);

  /** Bumped by every status-changed event and every re-check, so only the newest status lands. */
  const statusSeq = useRef(0);
  const conversationIdRef = useRef(conversation.conversationId);
  conversationIdRef.current = conversation.conversationId;

  const setAttachmentList = useCallback((next: HelpAttachment[]) => {
    attachmentsRef.current = next;
    setAttachments(next);
  }, []);

  // Status, pack and events.
  useEffect(() => {
    if (!help) return undefined;
    let live = true;
    const unsubscribe = help.onEvent((raw: unknown) => {
      const parsed = helpEventSchema.safeParse(raw);
      if (!live) return;
      if (!parsed.success) {
        // A turn-finished event that does not parse must still end the turn, or
        // the panel would wait on it forever.
        const loose = raw as { type?: unknown; conversationId?: unknown; turnId?: unknown } | null;
        if (
          loose?.type === "turn-finished" &&
          typeof loose.conversationId === "string" &&
          typeof loose.turnId === "string"
        ) {
          dispatch({
            type: "event",
            event: {
              type: "turn-finished",
              conversationId: loose.conversationId,
              turnId: loose.turnId,
              outcome: "failed",
              sources: [],
              links: [],
              actions: [],
            },
          });
        }
        return;
      }
      const event = parsed.data;
      if (event.type === "status-changed") {
        statusSeq.current += 1;
        setStatus(event.status);
        return;
      }
      dispatch({ type: "event", event });
    });
    help
      .getStatus()
      .then((raw: unknown) => {
        if (!live) return;
        const parsed = helpStatusSchema.safeParse(raw);
        if (parsed.success) setStatus((current) => current ?? parsed.data);
        else setStatusFailed(true);
      })
      .catch(() => {
        if (live) setStatusFailed(true);
      });
    help
      .getPackInfo()
      .then((raw: unknown) => {
        if (!live) return;
        const parsed = helpPackInfoSchema.safeParse(raw);
        if (parsed.success) setPackInfo(parsed.data);
      })
      .catch(() => undefined);
    return () => {
      live = false;
      unsubscribe();
    };
  }, [help]);

  // Ask the app again whether Help can answer (gap G-03). A late answer never
  // replaces a newer status-changed event's (`statusSeq`).
  const recheckStatus = useCallback(() => {
    if (!help) return;
    const seq = ++statusSeq.current;
    help
      .getStatus()
      .then((raw: unknown) => {
        const parsed = helpStatusSchema.safeParse(raw);
        if (parsed.success && seq === statusSeq.current) setStatus(parsed.data);
      })
      .catch(() => undefined);
  }, [help]);

  // While the open panel shows Claude signed out or missing, ask again every
  // few seconds and whenever the window gets the focus back (gap G-03): the
  // sign-in happens in the browser or Setup, which sends no event.
  const needsRecheck = panelOpen && helpStatusNeedsRecheck(status);
  useEffect(() => {
    if (!needsRecheck) return undefined;
    const timer = setInterval(recheckStatus, HELP_NOT_READY_RECHECK_MS);
    const onFocus = () => {
      recheckStatus();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") recheckStatus();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [needsRecheck, recheckStatus]);

  // "Signed in — Help is ready" once Help is ready again after a sign-out (gap G-03).
  const previousState = useRef<HelpStatus["state"] | null>(null);
  const statusState = status?.state ?? null;
  useEffect(() => {
    if (statusState === "ready" && previousState.current === "signed-out") setReadyNotice(HELP_SIGNED_IN_NOTICE);
    else if (statusState !== null && statusState !== "ready") setReadyNotice(null);
    previousState.current = statusState;
  }, [statusState]);

  // End the conversation (the app deletes its folder) when Help goes away.
  useEffect(() => {
    if (!help) return undefined;
    return () => {
      void help.endConversation(conversationIdRef.current).catch(() => undefined);
    };
  }, [help]);

  const support = toolsetSupportOf(bridge, version.data, "help");
  const availability = helpAvailabilityOf(bridge, support, status, statusFailed);

  const pathname = location.pathname;
  const screenKey = helpScreenKeyForPath(pathname);
  const project = projectFromPath(pathname);
  const roles = useMemo(() => helpRolesFor(access, project), [access, project]);
  const screenLabel = HELP_SCREEN_LABELS[screenKey];

  const serverVersion = version.data?.version;
  const packVersion = packInfo?.spectreeVersion;
  const packOlder =
    packVersion !== undefined &&
    serverVersion !== undefined &&
    compareDottedVersions(majorMinorOf(packVersion), majorMinorOf(serverVersion)) < 0;
  const verdictOutOfDate = conversation.messages.some(
    (message) => message.role === "help" && message.verdict === "out-of-date"
  );
  useEffect(() => {
    if (packOlder || verdictOutOfDate) setOutOfDateLatched(true);
  }, [packOlder, verdictOutOfDate]);
  const outOfDate =
    outOfDateLatched || packOlder || verdictOutOfDate ? { packVersion, serverVersion } : null;

  const asking = conversation.activeAnswerId !== null;

  const addImage = useCallback(
    (image: HelpImageInput): boolean => {
      if (attachmentsRef.current.length >= HELP_IMAGES_MAX) {
        setAttachError(HELP_IMAGE_LIMIT_MESSAGE);
        return false;
      }
      setAttachError(null);
      setAttachmentList([...attachmentsRef.current, attachmentOf(image)]);
      return true;
    },
    [setAttachmentList]
  );

  const addImageFiles = useCallback(
    async (files: readonly File[]) => {
      for (const file of files) {
        if (attachmentsRef.current.length >= HELP_IMAGES_MAX) {
          setAttachError(HELP_IMAGE_LIMIT_MESSAGE);
          return;
        }
        if (!isHelpImageMimeType(file.type)) {
          setAttachError(HELP_IMAGE_TYPE_MESSAGE);
          continue;
        }
        if (file.size <= 0 || file.size > HELP_IMAGE_BYTES_MAX) {
          setAttachError(HELP_IMAGE_SIZE_MESSAGE);
          continue;
        }
        let dataBase64: string;
        try {
          dataBase64 = await readFileAsBase64(file);
        } catch {
          setAttachError("That image could not be read. Try another one.");
          continue;
        }
        const name = (file.name.trim() || "screenshot").slice(0, 200);
        addImage({ name, mimeType: file.type, dataBase64 });
      }
    },
    [addImage]
  );

  const removeAttachment = useCallback(
    (id: string) => {
      setAttachError(null);
      setAttachmentList(attachmentsRef.current.filter((attachment) => attachment.id !== id));
    },
    [setAttachmentList]
  );

  const captureScreen = useCallback(async () => {
    if (!help) return;
    if (attachmentsRef.current.length >= HELP_IMAGES_MAX) {
      setAttachError(HELP_IMAGE_LIMIT_MESSAGE);
      return;
    }
    try {
      const result = await help.captureScreen();
      if (result.ok) addImage(result.image);
      else
        setAttachError(
          result.message.trim() ||
            "The screen could not be captured. Try pasting a screenshot instead."
        );
    } catch {
      setAttachError("The screen could not be captured. Try pasting a screenshot instead.");
    }
  }, [help, addImage]);

  const ask = useCallback(
    async (questionArg?: string) => {
      if (!help || availability !== "ready" || asking) return;
      const fromDraft = questionArg === undefined;
      const question = (questionArg ?? draft).trim();
      if (question.length === 0) return;
      const sent = attachmentsRef.current;
      const context: HelpContext = {
        route: pathname.slice(0, 512) || "/",
        pageTitle: screenLabel,
        roles,
        ...(project ? { project: project.slice(0, 256) } : {}),
        ...(() => {
          const itemId = helpItemIdFromLocation(pathname, location.search);
          return itemId ? { itemId } : {};
        })(),
        appVersion: bridge?.app.version ?? "unknown",
        ...(serverVersion ? { spectreeVersion: serverVersion.slice(0, 64) } : {}),
        ...(packVersion ? { packVersion: packVersion.slice(0, 64) } : {}),
      };
      const input = {
        conversationId: conversation.conversationId,
        question,
        images: sent.map((attachment) => attachment.image),
        context,
        messages: historyOf(conversation.messages),
      };
      const person: HelpPersonMessage = {
        id: nextId("help-person"),
        role: "person",
        text: question,
        images: sent,
        context,
      };
      const answerId = nextId("help-answer");
      dispatch({ type: "ask-started", person, answerId });
      setReadyNotice(null);
      // A suggested question leaves whatever the person has typed alone.
      if (fromDraft) setDraft("");
      setAttachError(null);
      setAttachmentList([]);

      const restore = () => {
        if (fromDraft) setDraft((current) => (current.length === 0 ? question : current));
        if (attachmentsRef.current.length === 0) setAttachmentList(sent);
      };
      const parsed = helpAskInputSchema.safeParse(input);
      if (!parsed.success) {
        dispatch({
          type: "ask-failed",
          answerId,
          errorCode: "UNKNOWN",
          message: "That question could not be sent. Shorten it and ask again.",
        });
        restore();
        return;
      }
      try {
        const result = await help.ask(parsed.data);
        if (result.ok) {
          dispatch({ type: "ask-resolved", answerId, turnId: result.turnId });
        } else {
          dispatch({
            type: "ask-failed",
            answerId,
            errorCode: result.errorCode,
            message: helpErrorMessage(result.errorCode, result.message),
          });
          restore();
        }
      } catch {
        dispatch({
          type: "ask-failed",
          answerId,
          errorCode: "UNKNOWN",
          message: helpErrorMessage("UNKNOWN"),
        });
        restore();
      }
    },
    [
      help,
      availability,
      asking,
      draft,
      pathname,
      location.search,
      screenLabel,
      roles,
      project,
      bridge,
      serverVersion,
      packVersion,
      conversation.conversationId,
      conversation.messages,
      setAttachmentList,
    ]
  );

  const stop = useCallback(() => {
    if (!help || !asking) return;
    void help.stop(conversation.conversationId).catch(() => undefined);
  }, [help, asking, conversation.conversationId]);

  const newConversation = useCallback(() => {
    const old = conversation.conversationId;
    if (help) {
      if (asking) void help.stop(old).catch(() => undefined);
      void help.endConversation(old).catch(() => undefined);
    }
    dispatch({ type: "reset", conversationId: newHelpConversationId() });
    setDraft("");
    setAttachError(null);
    setAttachmentList([]);
    setOutOfDateLatched(false);
    setReadyNotice(null);
  }, [help, asking, conversation.conversationId, setAttachmentList]);

  const getGuide = useCallback(
    async (id: string): Promise<HelpGuideResult> => {
      const unavailable: HelpGuideResult = {
        ok: false,
        errorCode: "UNKNOWN",
        message: "That guide could not be opened.",
      };
      if (!help) return unavailable;
      try {
        const result = await help.getGuide(id);
        if (!result.ok) return result;
        const guide = helpGuideSchema.safeParse(result.guide);
        return guide.success ? { ok: true, guide: guide.data } : unavailable;
      } catch {
        return unavailable;
      }
    },
    [help]
  );

  return {
    availability,
    status,
    packInfo,
    screenKey,
    screenLabel,
    project,
    roles,
    messages: conversation.messages,
    asking,
    draft,
    setDraft,
    attachments,
    attachError,
    addImageFiles,
    removeAttachment,
    captureScreen,
    outOfDate,
    ask,
    stop,
    newConversation,
    getGuide,
    recheckStatus,
    readyNotice,
  };
}
